import React from 'react';
import { TableCell, TableRow } from 'src/components/ui/table';
import { ChevronDown, ChevronRight, CircleUser, EyeOff, NotebookPen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { Invoice } from '../../../../redux/billing/billing.types';
import { formatNumber, formatCurrency, formatLastSynced } from './utils';
import StatusBadgeThin from './status-badge-thin';
import TextIconInput from '../text-icon-input';
import { Company } from 'src/pages/billing/types/billing.types';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/redux-store';

interface LineItemRowProps {
  isLastRow: boolean;
  lineItem: any;
  invoice: Invoice;
  invoiceIndex: number;
  isLineItemExpanded: boolean;
  viewHidden: boolean;
  companies: Company[];
  owner: string;
  filteredInvoices: Invoice[];
  toggleLineItemExpansion: (lineItemId: string) => void;
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
}

export const LineItemRow: React.FC<LineItemRowProps> = ({
  isLastRow,
  lineItem,
  invoice,
  invoiceIndex,
  isLineItemExpanded,
  viewHidden,
  companies,
  filteredInvoices,
  owner,
  toggleLineItemExpansion,
  updateLineItemField,
}) => {

  const userState = useSelector((state: RootState) => state.userState);

  const handleUpdateLineItemName = (value: string) => {
    updateLineItemField(lineItem.id, 'name', value);
  };

  const isOwner = () => {
    return owner === userState.email;
  };

  return (
    <TableRow
      key={lineItem.id}
      className={`bg-gray-200 transition-colors border-b border-gray-100 cursor-pointer border-yellow-500 border-l-4 border-r-4 border-b-gray-201 border-b-1  ${
        lineItem.attention_needed
          ? 'hover:bg-[#FFEBEE]'
          : 'hover:bg-gray-201'
      } ${
        isLineItemExpanded ? 'bg-gray-201 border-b border-b-gray-400 hover:bg-gray-400' : ''
      } ${
        isLastRow && !isLineItemExpanded ? 'border-b-4 border-b-yellow-500' : ''
      }`}
      onClick={() => toggleLineItemExpansion(lineItem.id)}
    >
      <TableCell
        colSpan={1}
        className={`py-1.5 w-[3%] min-w-[30px] cursor-pointer border-r border-gray-201 ${isLineItemExpanded ? 'border-r border-gray-400' : ''}`}
        onClick={() => toggleLineItemExpansion(lineItem.id)}
      >
        <div className="relative">
          {isLineItemExpanded ? (
            <ChevronDown className={`h-4 w-4 ${lineItem.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
          ) : (
            <ChevronRight className={`h-4 w-4 ${lineItem.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
          )}
        </div>
      </TableCell>
      <TableCell
        colSpan={1}
        className={`py-1.5 w-[35%] min-w-[250px] max-w-[250px] border-r border-gray-201 ${isLineItemExpanded ? 'border-r border-gray-400' : ''}`}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="min-w-0 flex-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex font-bold truncate ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
                    {lineItem.hide && viewHidden && (
                      <span className="flex font-bold items-center mr-2">
                        <EyeOff className="h-3 w-3 text-black" />
                      </span>
                    )}
                    {lineItem.notes && (
                      <span className="flex items-center mr-2">
                        <NotebookPen className="h-3 w-3 text-orange-500" />
                      </span>
                    )}
                    <TextIconInput
                      inputClassName='text-left font-bold truncate w-full ml-2'
                      inputContainerClassName='p-0'
                      value={lineItem.name}
                      onChange={(value) => handleUpdateLineItemName(value)}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-none py-2">
                  <p>
                    {lineItem.name}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className={`text-xs truncate ${lineItem.attention_needed ? 'text-[#CC0000]' : 'text-gray-500'}`}>
              {owner ? (
                <div className={`flex flex-row items-center gap-1 rounded-full w-fit px-1 ${isOwner() ? 'bg-blue-500 text-white' : 'bg-gray-201'}`}>
                  <CircleUser className="h-3 w-3" />
                  {owner}
                </div>
              ) : (formatLastSynced(lineItem.last_sync))}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className={`py-1.5 w-[15%] min-w-[70px] max-w-[70px] border-r border-gray-201 ${isLineItemExpanded ? 'border-r border-gray-400' : ''}`}>
        <div
          className={`truncate capitalize font-bold ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}
          title={lineItem.channel ? lineItem.channel.charAt(0).toUpperCase() + lineItem.channel.slice(1) : 'N/A'}
        >
          {lineItem.channel ? lineItem.channel.charAt(0).toUpperCase() + lineItem.channel.slice(1) : 'N/A'}
        </div>
      </TableCell>
      <TableCell className={`py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px] border-r border-gray-201 font-bold ${isLineItemExpanded ? 'border-r border-gray-400' : ''} ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
        {formatNumber(lineItem.qty_override || lineItem.qty)}
      </TableCell>
      <TableCell className={`py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px] border-r border-gray-201 font-bold ${isLineItemExpanded ? 'border-r border-gray-400' : ''} ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
        {formatCurrency(lineItem.amount_to_bill || 0)}
      </TableCell>
      <TableCell className="py-1.5 text-center w-[15%] min-w-[100px] max-w-[100px]">
        <StatusBadgeThin
          status={lineItem.invoice_line_item_status}
          attention_needed={lineItem.attention_needed}
        />
      </TableCell>
    </TableRow>
  );
};

export default LineItemRow;
